import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

// مكون Loading Spinner أساسي
export const LoadingSpinner = ({ size = 'medium', color = 'primary' }) => {
  const { theme } = useTheme();
  
  const sizes = {
    small: '20px',
    medium: '40px',
    large: '60px'
  };
  
  const colors = {
    primary: '#2196f3',
    secondary: '#666',
    white: '#fff'
  };
  
  return (
    <div 
      className="loading-spinner"
      style={{
        width: sizes[size],
        height: sizes[size],
        border: `3px solid ${theme === 'dark' ? '#333' : '#f3f3f3'}`,
        borderTop: `3px solid ${colors[color]}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  );
};

// مكون Loading مع نص
export const LoadingWithText = ({ text, size = 'medium' }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  
  const defaultText = isArabic ? 'جاري التحميل...' : 'Loading...';
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      gap: '16px'
    }}>
      <LoadingSpinner size={size} />
      <p style={{ 
        margin: 0, 
        color: '#666',
        fontSize: '14px',
        textAlign: 'center'
      }}>
        {text || defaultText}
      </p>
    </div>
  );
};

// مكون Skeleton Loading للمحتوى
export const SkeletonLoader = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  count = 1,
  spacing = '8px'
}) => {
  const { theme } = useTheme();
  
  const skeletonStyle = {
    width,
    height,
    borderRadius,
    backgroundColor: theme === 'dark' ? '#333' : '#f0f0f0',
    animation: 'skeleton-loading 1.5s ease-in-out infinite alternate',
    marginBottom: count > 1 ? spacing : '0'
  };
  
  return (
    <div className="skeleton-container">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} style={skeletonStyle} />
      ))}
    </div>
  );
};

// مكون Page Loading للصفحات الكاملة
export const PageLoading = ({ message }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  
  const defaultMessage = isArabic ? 'جاري تحميل الصفحة...' : 'Loading page...';
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(2px)'
    }}>
      <LoadingWithText text={message || defaultMessage} size="large" />
    </div>
  );
};

// مكون Button Loading
export const ButtonLoading = ({ loading, children, ...props }) => {
  return (
    <button 
      {...props}
      disabled={loading || props.disabled}
      style={{
        ...props.style,
        position: 'relative',
        opacity: loading ? 0.7 : 1
      }}
    >
      {loading && (
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}>
          <LoadingSpinner size="small" color="white" />
        </div>
      )}
      <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
        {children}
      </span>
    </button>
  );
};

// إضافة CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes skeleton-loading {
    0% { opacity: 0.6; }
    100% { opacity: 1; }
  }
`;
document.head.appendChild(style);

export default LoadingSpinner;